import React, { KeyboardEventHandler } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import { versionDependent } from '../../images/versionDependentExport';
import { Trustmarks } from '../Trustmarks/Trustmarks';
import {
  ChevronInACircle as ChevronInACircleIcon,
} from '../icons/ChevronInACircle/ChevronInACircle';
import { Close as CloseIcon } from '../icons/Close/Close';
import { SearchLoupe as SearchLoupeIcon } from '../icons/SearchLoupe/SearchLoupe';
import styles from './SearchInput.module.css';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'src.components.SearchInput.searchPlaceholder',
    defaultMessage: 'What are you looking for?',
  },
});

const iconsColor = {
  white: 'var(--search-input-icon-color)',
  grey: '#0f0a1b',
};

export const hooks = {
  useValue(
    onChange: (search: string) => void,
    initialValue: string | null | undefined = undefined,
  ) {
    const search = routerHooks.useQueryParams()?.s || '';
    const [value, setValue] = React.useState<string>(initialValue || '');
    React.useEffect(() => {
      if (initialValue && search !== '') {
        setValue(initialValue);
      }
    }, [initialValue, search]);

    React.useEffect(() => {
      if (search === '') {
        setValue('');
      }
    }, [search]);

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputText = e.target.value;
        setValue(inputText);
        onChange(inputText);
      },
      [setValue, onChange],
    );

    const handleClear = React.useCallback(() => {
      setValue('');
      onChange('');
    }, [setValue, onChange]);

    return {
      value,
      handleChange,
      handleClear,
    };
  },
  useStopPropagationClick() {
    return React.useCallback((event: React.SyntheticEvent) => {
      event.stopPropagation();
    }, []);
  },
  useOnClear(
    onClearSearch: (() => void) | undefined,
    handleClear: () => void,
  ) {
    return React.useCallback(() => {
      if (onClearSearch) {
        onClearSearch();
      }
      handleClear();
    }, [handleClear, onClearSearch]);
  },
};

export type Props = {
  className?: string;
  dataTau?: string;
  hideSearchModal?(): void;
  isSearchModalShown: boolean;
  onBlur?(): void;
  onChange(search: string): void;
  onClearSearch?: () => void;
  onFocus?(): void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  placeHolder?: string;
  value?: string | null;
  variant: 'white' | 'grey';
};

export const SearchInput = React.memo((props: Props) => {
  const {
    isSearchModalShown,
    onChange,
    onFocus,
    onBlur,
    hideSearchModal,
    className,
    variant,
    placeHolder,
    onKeyDown,
    value: initialValue,
    onClearSearch,
    dataTau,
  } = props;

  const handleStopPropagation = hooks.useStopPropagationClick();

  const { value, handleChange, handleClear } = hooks.useValue(onChange, initialValue);

  const onClear = hooks.useOnClear(onClearSearch, handleClear);

  const intl = useIntl();
  const { isClient } = hydrationHooks.useIsClient();

  return (
    <div className={classNames(styles.searchInput, styles[variant], className)}>
      <div data-tau={dataTau} className={styles.searchInputInner}>
        <div
          className={classNames(styles.iconAndInputWrapper, {
            [styles.active]: isSearchModalShown,
          })}
        >
          {isSearchModalShown
            ? <ChevronInACircleIcon
              color={iconsColor[variant]}
              className={styles.circleAndLeftPointedArrow}
              onClick={hideSearchModal}
            />
            : <SearchLoupeIcon color={iconsColor[variant]} className={styles.loupe} />}

          <input
            className={styles.input}
            placeholder={placeHolder || intl.formatMessage(messages.searchPlaceholder)}
            value={value}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={handleStopPropagation}
            onKeyDown={onKeyDown}
            disabled={!isClient}
          />
          {value.length > 0
            && <CloseIcon
              className={styles.clearIcon}
              color={iconsColor[variant]}
              width={16}
              height={16}
              onClick={onClear}
            />}
        </div>
        <Trustmarks
          trustMarks={versionDependent.trustMarksTopbar}
          className={styles.trustmarks}
        />
      </div>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';
